import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getGenericUserInfo } from '../../../features/sessions/sessionProfileNavigation';
import { UserAvatar } from '../../userAvatar/UserAvatar';
import { SelectProjectModal } from '../../modal/attachProjectSelectionModal/SelectProjectModal';
import { format } from 'date-fns';
import { ProjectPlayer } from '../../audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { createPost } from '../../../features/sessions/sessionPostProject/sessionPostProject';
import React from 'react';
import { toast } from 'react-toastify';
import { getUserProjects } from '../../../features/sessions/projectsManagement/sessionManageProjects';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_CARD,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputBase-inputMultiline': { color: TEXT_PRIMARY },
};

interface ProjectSelectModalProps {
    onOpenModal: Dispatch<SetStateAction<boolean>>;
    modalOpened: boolean;
    projectSelectedId: number | undefined;
    setProjectSelectedId: Dispatch<SetStateAction<number | undefined>>;
}

export function PostGroupCreate({ onOpenModal, modalOpened, projectSelectedId, setProjectSelectedId }: ProjectSelectModalProps) {
    const authHeaders            = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser            = useSelector((state: RootState) => state.session.currentUser);
    const allLoggedUserProjects  = useSelector((state: RootState) => state.sessionManageProjects.allProjects);
    const avatar                 = useSelector((state: RootState) => state.session.userMoreInfo.avatarUrl);
    const dispatch               = useDispatch<AppDispatch>();
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);
    const [inputPostDescriptionForm, setInputPostDescriptionForm] = useState<string>('');
    const { userNicknameRoute, groupId } = useParams();
    const [userProjectsPage, setUserProjectsPage] = useState<number>(1);

    useEffect(() => {
        function fetchUserProjects() {
            if (authHeaders && userNicknameRoute) {
                dispatch(
                    getUserProjects({
                        authHeaders: authHeaders,
                        urlParams: { userNickname: userNicknameRoute, page: userProjectsPage },
                    })
                );
            }
        }

        function getUserMoreInfo() {
            if (authHeaders && userNicknameRoute && currentUser && currentUser.nickname) {
                dispatch(getGenericUserInfo({ authHeaders: authHeaders, userNickname: currentUser.nickname }));
            }
        }

        fetchUserProjects();
        getUserMoreInfo();
    }, [userNicknameRoute, modalOpened]);

    function handleFormatDate(date: any) {
        const originalDate = new Date(date);
        return format(originalDate, 'dd MMM yyyy HH:mm:ss');
    }

    async function handleCreatePost() {
        if (authHeaders && currentUser?.nickname) {
            const response = await dispatch(
                createPost({
                    authHeaders: authHeaders,
                    createPostRouteParams: {
                        userNickname: currentUser.nickname,
                        groupId: `${groupId}`,
                    },
                    body: {
                        userId: currentUser.id,
                        groupId: groupId,
                        projectId: projectSelectedId,
                        body: inputPostDescriptionForm,
                    },
                })
            );

            if (response && response.meta.requestStatus === 'fulfilled') {
                setProjectSelectedId(undefined);
                setInputPostDescriptionForm('');
            }

            if (response.payload.errors && response.payload.errors.length > 0) {
                response.payload.errors.map((item: string) => { toast.error(item); });
            }
        }
    }

    const selectedProject = allLoggedUserProjects.find(p => p.project.id === projectSelectedId);
    const selectedMasterFile = selectedProject?.masterFiles?.[0];

    return (
        <Box
            sx={{
                bgcolor: BG_SECTION,
                border: BORDER_SUBTLE,
                borderRadius: '10px',
                overflow: 'hidden',
            }}
        >
            {/* Área de texto */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: { xs: 1.5, sm: 2 }, pt: 2 }}>
                <UserAvatar src={avatar} size={38} />
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder='Share something with the group...'
                    value={inputPostDescriptionForm}
                    onChange={(e) => setInputPostDescriptionForm(e.target.value)}
                    inputProps={{ maxLength: 248 }}
                    size='small'
                    sx={inputSx}
                />
            </Box>

            {/* Projecto seleccionado */}
            {projectSelectedId && selectedProject ? (
                <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
                    <Box
                        sx={{
                            bgcolor: BG_CARD,
                            borderRadius: '8px',
                            border: BORDER_SUBTLE,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                pt: 1.25,
                                pb: 1,
                            }}
                        >
                            <Typography
                                variant='subtitle2'
                                fontWeight={600}
                                color={TEXT_PRIMARY}
                                noWrap
                                sx={{ flex: 1, minWidth: 0 }}
                            >
                                {selectedProject.project.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={selectedProject.project.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                                    size='small'
                                    sx={{
                                        bgcolor: selectedProject.project.privacity === 0
                                            ? 'rgba(35,148,0,0.18)'
                                            : 'rgba(192,57,43,0.18)',
                                        color: selectedProject.project.privacity === 0 ? '#4caf50' : '#e57373',
                                        fontWeight: 700,
                                        fontSize: '0.6rem',
                                    }}
                                />
                                <IconButton
                                    size='small'
                                    onClick={() => setProjectSelectedId(undefined)}
                                    sx={{ color: TEXT_SECONDARY, '&:hover': { color: '#e57373' } }}
                                >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        </Box>

                        {selectedMasterFile && (
                            <Box sx={{ px: 1.5, pb: 1.5 }}>
                                <Typography variant='caption' color={TEXT_SECONDARY} sx={{ display: 'block', mb: 0.5 }}>
                                    {handleFormatDate(selectedMasterFile.createdAt)}
                                </Typography>
                                <MemorizedProjectPlayer fileUrl={`${selectedMasterFile.signedUrl}`} />
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
                    <Typography variant='caption' color={TEXT_SECONDARY}>
                        No project attached
                    </Typography>
                </Box>
            )}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mt: 1.5 }} />

            {/* Acções */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 1.5, sm: 2 },
                    py: 1,
                }}
            >
                <Button
                    variant='outlined'
                    size='small'
                    startIcon={<AttachFileIcon />}
                    onClick={() => onOpenModal(!modalOpened)}
                    sx={{
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: TEXT_SECONDARY,
                        textTransform: 'none',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.4)', color: TEXT_PRIMARY },
                    }}
                >
                    {projectSelectedId ? 'Change project' : 'Attach project'}
                </Button>

                <Button
                    variant='contained'
                    size='small'
                    endIcon={<SendIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={handleCreatePost}
                    sx={{
                        bgcolor: BRAND_RED,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#a93226' },
                    }}
                >
                    Post
                </Button>
            </Box>
        </Box>
    );
}
